// src/pages/ForgotPassword.js// src/pages/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Meteors } from '../components/ui/meteors';

import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Mail, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const { 
    user, 
    userLoading, 
    isAuthLoading, 
    handleLogout, 
    openLogin, 
    openRegister 
  } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API_URL}/accounts/password_reset/`, 
        { email },
        { withCredentials: true }
      );

      if (response.data.success) {
        setMessage("We've sent password reset instructions to your email. Please check your inbox (and spam folder).");
        setEmail('');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.errors?.all?.[0] 
        || err.response?.data?.error 
        || 'Failed to send reset email. Please try again.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Close any open modals first for smooth flow
    // (Assuming your AuthContext has a way to close modals - if not, this still works)
    openLogin(); 
    // Optional: small delay for better UX if modals overlap
    setTimeout(() => {
      navigate('/', { state: { showLogin: true } });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Meteors Background */}
      <div className="absolute inset-0 z-[-1] pointer-events-none overflow-hidden">
        <Meteors count={30} angle={220} />
      </div>

      {/* Navbar */}
      <Navbar
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={openLogin}
        openRegister={openRegister}
      />

      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="border-border shadow-lg">
            <CardHeader className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-semibold tracking-tight">
                Forgot Password
              </CardTitle>
              <CardDescription className="text-base">
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="yourname@example.com"
                    required
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>

                {error && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {message && (
                  <Alert className="mt-4 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
                    <AlertDescription className="text-green-700 dark:text-green-400">
                      {message}
                    </AlertDescription>
                  </Alert>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-11 text-base font-medium mt-6"
                  disabled={isLoading || !email.trim()}
                >
                  {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
                </Button>
              </form>

              {/* Back to Login - Uses openLogin from context */}
              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={handleBackToLogin}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 mx-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Didn't receive the email? Check your spam folder or contact support.
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
